import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Alert,
  Skeleton,
  alpha,
  Collapse,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  SportsFootball as FootballIcon,
  SportsBasketball as BasketballIcon,
  SportsSoccer as SoccerIcon,
  SportsHockey as HockeyIcon,
  Stadium as StadiumIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  AutoAwesome as LiveIcon,
} from '@mui/icons-material';
import { sportsApi } from '../api';
import type { SportConfig, ExternalGame, TeamMapping } from '../types';

export interface GameSelectorProps {
  onGameSelect: (game: ExternalGame, teamMappings: TeamMapping[]) => void;
  selectedGameId?: string;
}

const sportIcons: Record<string, React.ReactNode> = {
  nfl: <FootballIcon />,
  ncaaf: <FootballIcon />,
  nba: <BasketballIcon />,
  ncaab: <BasketballIcon />,
  wnba: <BasketballIcon />,
  soccer: <SoccerIcon />,
  hockey: <HockeyIcon />,
};

function formatGameDate(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);

  const isToday = date.toDateString() === now.toDateString();
  const isTomorrow = date.toDateString() === tomorrow.toDateString();

  const timeStr = date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

  if (isToday) return `Today @ ${timeStr}`;
  if (isTomorrow) return `Tomorrow @ ${timeStr}`;

  return date.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' }) + ` @ ${timeStr}`;
}

function getStatusChip(status: string) {
  switch (status) {
    case 'in_progress':
      return <Chip label="LIVE" size="small" sx={{ bgcolor: '#ef4444', color: '#fff', fontWeight: 700, fontSize: '0.65rem', letterSpacing: '0.05em', animation: 'pulse 2s infinite', '@keyframes pulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.7 } } }} />;
    case 'final':
      return <Chip label="Final" size="small" variant="outlined" sx={{ fontSize: '0.65rem' }} />;
    case 'postponed':
      return <Chip label="PPD" size="small" color="warning" sx={{ fontSize: '0.65rem' }} />;
    case 'canceled':
      return <Chip label="CXL" size="small" color="error" variant="outlined" sx={{ fontSize: '0.65rem' }} />;
    default:
      return null;
  }
}

export const GameSelector: React.FC<GameSelectorProps> = ({ onGameSelect, selectedGameId }) => {
  const [sports, setSports] = useState<SportConfig[]>([]);
  const [selectedSportIdx, setSelectedSportIdx] = useState(0);
  const [games, setGames] = useState<ExternalGame[]>([]);
  const [teamMappings, setTeamMappings] = useState<TeamMapping[]>([]);
  const [loading, setLoading] = useState(true);
  const [gamesLoading, setGamesLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(true);

  // Load enabled sports on mount
  useEffect(() => {
    const loadSports = async () => {
      try {
        setLoading(true);
        const configs = await sportsApi.getEnabledSports();
        setSports(configs);
        setError(null);
      } catch {
        setError('Could not load live sports');
      } finally {
        setLoading(false);
      }
    };
    loadSports();
  }, []);

  // Load games + teams when sport tab changes
  const selectedSport = sports[selectedSportIdx];
  useEffect(() => {
    if (!selectedSport) return;
    const loadGames = async () => {
      try {
        setGamesLoading(true);
        const [fetchedGames, fetchedTeams] = await Promise.all([
          sportsApi.getUpcomingGames(selectedSport.sportKey),
          sportsApi.getTeamMappings(selectedSport.sportKey),
        ]);
        setGames(fetchedGames);
        setTeamMappings(fetchedTeams);
      } catch {
        setGames([]);
        setTeamMappings([]);
      } finally {
        setGamesLoading(false);
      }
    };
    loadGames();
  }, [selectedSport]);

  // Group games by date
  const groupedGames = useMemo(() => {
    const groups: Record<string, ExternalGame[]> = {};
    for (const game of games) {
      const dateKey = new Date(game.gameDate).toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric' });
      if (!groups[dateKey]) groups[dateKey] = [];
      groups[dateKey].push(game);
    }
    return groups;
  }, [games]);

  const getTeamMapping = (teamId: string) => {
    return teamMappings.find(t => t.externalTeamId === teamId);
  };

  const getTeamColor = (teamId: string): string | undefined => {
    return getTeamMapping(teamId)?.primaryColor || undefined;
  };

  const getTeamAbbr = (teamId: string, fallback: string): string => {
    return getTeamMapping(teamId)?.abbreviation || fallback;
  };

  const getTeamLogo = (teamId: string): string | undefined => {
    return getTeamMapping(teamId)?.logoUrl || undefined;
  };

  if (loading) {
    return (
      <Box sx={{ p: 2 }}>
        <Skeleton variant="rectangular" height={48} sx={{ borderRadius: 1, mb: 1 }} />
        <Skeleton variant="rectangular" height={80} sx={{ borderRadius: 1 }} />
      </Box>
    );
  }

  if (error || sports.length === 0) {
    return null; // Silently hide if no live sports configured
  }

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: alpha('#6366f1', 0.25),
        borderRadius: 2,
        overflow: 'hidden',
        mb: 2,
        background: (theme) => `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.02)} 0%, ${alpha('#6366f1', 0.04)} 100%)`,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1,
          borderBottom: expanded ? '1px solid' : 'none',
          borderColor: 'divider',
          cursor: 'pointer',
          '&:hover': { bgcolor: alpha('#6366f1', 0.04) },
          transition: 'background-color 0.15s',
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LiveIcon sx={{ color: '#6366f1', fontSize: 20 }} />
          <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#6366f1', letterSpacing: '-0.01em' }}>
            Pick from Live Schedule
          </Typography>
          <Chip
            label="NEW"
            size="small"
            sx={{
              bgcolor: alpha('#6366f1', 0.1),
              color: '#6366f1',
              fontWeight: 700,
              fontSize: '0.6rem',
              height: 18,
              letterSpacing: '0.08em',
            }}
          />
        </Box>
        <IconButton size="small">
          {expanded ? <CollapseIcon /> : <ExpandIcon />}
        </IconButton>
      </Box>

      <Collapse in={expanded}>
        {/* Sport Tabs */}
        <Tabs
          value={selectedSportIdx}
          onChange={(_, v) => setSelectedSportIdx(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            minHeight: 40,
            px: 1,
            borderBottom: '1px solid',
            borderColor: 'divider',
            '& .MuiTab-root': {
              minHeight: 40,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.8rem',
              gap: 0.5,
            },
          }}
        >
          {sports.map((sport) => (
            <Tab
              key={sport.sportKey}
              icon={sportIcons[sport.sportKey] || <StadiumIcon />}
              iconPosition="start"
              label={sport.displayName}
            />
          ))}
        </Tabs>

        {/* Games List */}
        <Box sx={{ maxHeight: 320, overflowY: 'auto', p: 1.5 }}>
          {gamesLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={28} />
            </Box>
          ) : games.length === 0 ? (
            <Alert severity="info" sx={{ fontSize: '0.8rem' }}>
              No upcoming games found for {selectedSport?.displayName}. An admin may need to sync the schedule.
            </Alert>
          ) : (
            Object.entries(groupedGames).map(([dateLabel, dateGames]) => (
              <Box key={dateLabel} sx={{ mb: 1.5, '&:last-child': { mb: 0 } }}>
                <Typography
                  variant="caption"
                  fontWeight={700}
                  color="text.secondary"
                  sx={{ px: 0.5, mb: 0.5, display: 'block', textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: '0.65rem' }}
                >
                  {dateLabel}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                  {dateGames.map((game) => {
                    const isSelected = selectedGameId === game.id;
                    const isLive = game.status === 'in_progress';
                    const isUnavailable = isLive || game.status === 'final' || game.status === 'canceled';
                    const awayColor = getTeamColor(game.awayTeamId);
                    const homeColor = getTeamColor(game.homeTeamId);

                    return (
                      <Card
                        key={game.id}
                        variant="outlined"
                        sx={{
                          borderColor: isSelected ? '#6366f1' : 'divider',
                          borderWidth: isSelected ? 2 : 1,
                          bgcolor: isSelected ? alpha('#6366f1', 0.04) : 'transparent',
                          opacity: isUnavailable ? 0.5 : 1,
                          transition: 'all 0.15s',
                        }}
                      >
                        <CardActionArea
                          onClick={() => !isUnavailable && onGameSelect(game, teamMappings)}
                          disabled={isUnavailable}
                          sx={{ p: 1.25 }}
                        >
                          <CardContent sx={{ p: '0 !important' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                              {/* Teams */}
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 0 }}>
                                {/* Away */}
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                                  {getTeamLogo(game.awayTeamId) ? (
                                    <Box
                                      component="img"
                                      src={getTeamLogo(game.awayTeamId)}
                                      alt={game.awayTeam}
                                      onError={(e: React.SyntheticEvent<HTMLImageElement>) => { e.currentTarget.style.display = 'none'; }}
                                      sx={{ width: 20, height: 20, objectFit: 'contain', flexShrink: 0 }}
                                    />
                                  ) : awayColor ? (
                                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: awayColor, flexShrink: 0 }} />
                                  ) : null}
                                  <Tooltip title={game.awayTeam} placement="top">
                                    <Typography variant="body2" fontWeight={700} noWrap sx={{ fontSize: '0.8rem' }}>
                                      {getTeamAbbr(game.awayTeamId, game.awayTeam)}
                                    </Typography>
                                  </Tooltip>
                                </Box>

                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                  @
                                </Typography>

                                {/* Home */}
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                                  {getTeamLogo(game.homeTeamId) ? (
                                    <Box
                                      component="img"
                                      src={getTeamLogo(game.homeTeamId)}
                                      alt={game.homeTeam}
                                      onError={(e: React.SyntheticEvent<HTMLImageElement>) => { e.currentTarget.style.display = 'none'; }}
                                      sx={{ width: 20, height: 20, objectFit: 'contain', flexShrink: 0 }}
                                    />
                                  ) : homeColor ? (
                                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: homeColor, flexShrink: 0 }} />
                                  ) : null}
                                  <Tooltip title={game.homeTeam} placement="top">
                                    <Typography variant="body2" fontWeight={700} noWrap sx={{ fontSize: '0.8rem' }}>
                                      {getTeamAbbr(game.homeTeamId, game.homeTeam)}
                                    </Typography>
                                  </Tooltip>
                                </Box>
                              </Box>

                              {/* Score (if in progress) */}
                              {game.status === 'in_progress' && game.awayScore != null && game.homeScore != null && (
                                <Typography variant="body2" fontWeight={700} sx={{ mx: 1, fontFamily: 'monospace', fontSize: '0.8rem' }}>
                                  {game.awayScore} - {game.homeScore}
                                </Typography>
                              )}

                              {/* Time / Status */}
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                                {getStatusChip(game.status)}
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', whiteSpace: 'nowrap' }}>
                                  {game.status === 'scheduled' ? formatGameDate(game.gameDate) : ''}
                                </Typography>
                              </Box>
                            </Box>

                            {/* Venue */}
                            {game.venue && (
                              <Typography variant="caption" color="text.disabled" sx={{ mt: 0.25, display: 'block', fontSize: '0.65rem' }}>
                                {game.venue}
                                {game.week != null && ` · Week ${game.week}`}
                              </Typography>
                            )}
                          </CardContent>
                        </CardActionArea>
                      </Card>
                    );
                  })}
                </Box>
              </Box>
            ))
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

export default GameSelector;
